export const umamiNetworks = {
  PREVIEW: 'd1c383fe-8226-4640-a1bf-906b1ee02075',
  PREPROD: '43a5fa1d-01da-4ecf-9d18-af2ece375653',
  MAINNET: '8d968427-3d64-4db1-86c5-3cad6a10785f',
};
const domainMapping = {
  'preview.drep.id': 'PREVIEW',
  'preprod.drep.id': 'PREPROD',
  'drep.id': 'MAINNET',
  'dev.drep.id': 'PREVIEW',
};

/**
 * Which Umami property a host reports into, keyed by host so it needs no
 * configuration and is correct in the browser on a statically prerendered
 * page. Read by the injector in `components/molecules/script.tsx` and by
 * `app/auth/route.ts`, which must agree.
 *
 * Deliberately separate from `domainMapping` above, which answers a different
 * question — which Cardano network a host talks to — and must keep its
 * `dev.drep.id` entry, because dropping it would send dev's `NETWORK_ID` to
 * the mainnet wallet gate. `dev.drep.id` is absent here instead: dev is a
 * staging host whose traffic is not real usage. A host that is not listed gets
 * no tracker at all, which also covers localhost and ad-hoc preview URLs.
 */
export const umamiSiteByHost: Record<string, string> = {
  'preview.drep.id': umamiNetworks.PREVIEW,
  'preprod.drep.id': umamiNetworks.PREPROD,
  'drep.id': umamiNetworks.MAINNET,
};

/**
 * The Elastic APM `environment` label per host. The network in lower case,
 * with `-dev` appended for the dev host, so dev reports `preview-dev` and not
 * `preview`: it talks to the preview network but is not the preview
 * deployment. Matches what the compose files set in `ELASTIC_APM_ENVIRONMENT`
 * for the server agent, so browser and server spans carry the same label.
 * Read by the inline bootstrap in `app/layout.tsx`, where it is what makes
 * the statically prerendered pages label themselves correctly.
 */
export const apmEnvironmentByHost: Record<string, string> = Object.fromEntries(
  Object.entries(domainMapping).map(([host, network]) => [
    host,
    host.startsWith('dev.')
      ? `${network.toLowerCase()}-dev`
      : network.toLowerCase(),
  ])
);

const website = (() => {
  if (typeof window === 'undefined') return undefined;
  return window.location.hostname;
})();

const isDev = website && website.includes('dev');

const websiteNetwork = (domainMapping as Record<string, string>)[
  website as string
] as string | undefined;

/**
 * The request host wins over any build-time value.
 *
 * CI builds one image and deploys it to mainnet, preprod, preview and dev, and
 * the Dockerfile bakes `NEXT_PUBLIC_CARDANO_NETWORK=mainnet` so the statically
 * prerendered SEO pages get the right canonicals. Webpack inlines that as a
 * string literal into the client bundle, so reading it first made every
 * testnet deployment call itself mainnet — which set NETWORK_ID to 1 (the
 * wallet network gate), pointed explorer links at mainnet, and reported
 * testnet traffic under the mainnet Umami site.
 *
 * `domainMapping` above is correct at request time and needs no configuration.
 * `CARDANO_NETWORK` (no NEXT_PUBLIC_ prefix, so read from the real environment
 * on the server) covers server rendering, where there is no `window`.
 */
const networkName = (
  websiteNetwork ||
  process.env.CARDANO_NETWORK ||
  process.env.NEXT_PUBLIC_CARDANO_NETWORK ||
  'PREVIEW'
).toUpperCase();

/**
 * This was `A || B || websiteNetwork ? f(websiteNetwork) : undefined`, which
 * parses as `(A || B || C) ? f(C) : undefined` — `||` binds tighter than
 * `?:` — so the env vars could only decide whether the branch was taken, never
 * supply the value, and during server rendering (no `window`) it was always
 * `undefined`.
 *
 * `ELASTIC_APM_ENVIRONMENT` has no `NEXT_PUBLIC_` prefix on purpose: prefixed
 * variables are inlined at build time, and one image serves four networks.
 * This value is only as fresh as the render that produced it, so on the
 * statically prerendered pages it is the build machine's, which is unset. The
 * inline bootstrap in `app/layout.tsx` resolves the label from the request
 * host first, through `apmEnvironmentByHost`, and uses this as its fallback.
 */
const apmEnv: string | undefined =
  (websiteNetwork
    ? isDev
      ? `${websiteNetwork}-dev`
      : websiteNetwork
    : undefined) ||
  process.env.ELASTIC_APM_ENVIRONMENT ||
  process.env.NEXT_PUBLIC_APM_ENV ||
  process.env.NEXT_PUBLIC_APM_ENVIRONMENT ||
  undefined;

const environments = {
  // api host configs
  IS_IN_PRODUCTION_MODE:
    process.env.NEXT_PUBLIC_NODE_ENV === 'production' ||
    process.env.NEXT_PUBLIC_NODE_ENV === 'prod',
  BASE_API_URL: process.env.NEXT_PUBLIC_BASE_API_URL || '/api',
  INTERNAL_API_URL: process.env.INTERNAL_API_URL,
  /**
   * Read the unprefixed runtime variable first, then the build-time flag.
   *
   * They were `!!umamiId || !!website` and `!!website`, and `website` is
   * `undefined` during server rendering by construction — so both evaluated
   * false on the server and true in the browser, and the two script tags
   * appeared in `<head>` only after hydration, against markup React had
   * already committed. They also switched tracking on for localhost.
   *
   * `UMAMI_ENABLED` / `APM_ENABLED` carry no `NEXT_PUBLIC_` prefix, so they
   * are read from the container environment per deployment and a network can
   * be switched off without a rebuild. They are authoritative on every
   * dynamically rendered route.
   *
   * The `NEXT_PUBLIC_` fallbacks are not dead: the statically prerendered
   * pages (`/`, `/learn`, `/glossary`, `/drep/mint`, `/docs/*`, `/_not-found`)
   * are rendered during `next build`, where the unprefixed variables do not
   * exist, so the build flag — passed as a build arg by the compose file the
   * image is built from — is what decides whether the bootstrap appears in
   * their HTML at all. Turning a network off at runtime therefore stops
   * tracking on dynamic routes immediately; on the prerendered pages the only
   * switch is the host map in `umamiSiteByHost`.
   */
  enableUmami:
    (process.env.UMAMI_ENABLED ?? process.env.NEXT_PUBLIC_UMAMI_ENABLED) ===
    'true',
  enableAPM:
    (process.env.APM_ENABLED ?? process.env.NEXT_PUBLIC_APM_ENABLED) === 'true',
  apmEnvironment: apmEnv,
  METADATA_API_URL: process.env.METADATA_API_URL || 'https://metadata.drep.id',
  NETWORK_ID:
    process.env.NEXT_PUBLIC_NETWORK_ID ||
    (networkName && networkName === 'MAINNET' ? '1' : '0'),
  CARDANO_NETWORK: networkName,
};

export default environments;
